import { LockOutlined } from '@ant-design/icons';
import { ScopeButton } from '@monorepo/shared-lib';
import { Col, Divider, Form, Row, Select, Typography } from 'antd';
import { FormInput } from 'components/Custom/inputs';
import { LabelWrapper } from 'components/Custom/labels';
import { FormSelect } from 'components/Custom/selects';
import { UpgradeMessage } from 'containers/UpgradeMessage';
import { NewMember } from 'pages/users';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { TablePermission, UpgradeMessageSource } from 'store/api/enums';
import { turnDrawerOff, turnModalOff, turnModalOn } from 'store/app/actions';
import { WindowType } from 'store/app/enums';
import { User } from 'store/auth/models';
import { getAuthUser, getAuthUserLicence } from 'store/auth/selectors';
import { getCurrentSubscription } from 'store/billing/selectors';
import {
  addMemberRequest,
  fetchUserRightsRequest,
  setUserRightsRequest,
} from 'store/user/actions';
import { getUserRights } from 'store/user/selectors';
import { RenderUserDetail } from 'utils/render-user-details';
import { isSmallOrMediumPlanLocked } from 'utils/subscriptionPlanGate';

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
}
interface InviteUserProps {
  loading: boolean;
  //saveNewMember: (newMember: NewMember) => void;
  allUsers?: User[];
  members?: User[];
  organisationId: string;
}

export const InviteUser: React.FC<InviteUserProps> = ({
  loading,
  //saveNewMember,
  allUsers,
  members,
  organisationId,
}) => {
  const SAVING = 'SAVING';
  const DELETING = 'DELETING';
  const dispatch = useDispatch();
  const rights = useSelector(getUserRights);
  const currentSubscription = useSelector(getCurrentSubscription);
  const authUser = useSelector(getAuthUser);
  const userLicence = useSelector(getAuthUserLicence);
  const isClientDisabled = isSmallOrMediumPlanLocked(
    currentSubscription,
    userLicence
  );

  const handleClientFlowUpgradeModal = () => {
    dispatch(
      turnModalOn({
        title: '',
        type: WindowType.WARNING,
        component: (
          <UpgradeMessage
            source={UpgradeMessageSource.CLIENT_FLOW}
            onClose={() => dispatch(turnModalOff())}
            userAccountType={authUser?.accountType}
          />
        ),
      })
    );
  };

  const { t } = useTranslation();
  const [isClient, setIsClient] = React.useState<boolean>(false);
  const [accountId, setAccountId] = React.useState<string>();
  const [showInvitationForm, setShowInvitationForm] =
    React.useState<boolean>(false);
  const [actionTaken, setActionTaken] = React.useState<string | null>(null);
  const [newMemberUser, setNewMemberUser] = React.useState<User>();

  React.useEffect(() => {
    if (isClientDisabled && isClient) {
      setIsClient(false);
    }
  }, [isClientDisabled, isClient]);

  React.useEffect(() => {
    if (actionTaken && rights) {
      if (actionTaken === DELETING) {
        const rightToChange = rights?.find(
          (r) =>
            r.accountUuid == accountId && r.organisationId == organisationId
        );
        const memberUser = members?.find((m) => m.accountUuid === accountId);
        rightToChange &&
          dispatch(
            setUserRightsRequest(
              rightToChange,
              TablePermission.NONE,
              memberUser
            )
          );
        const idx = members?.findIndex((m) => m.accountUuid === accountId);
        idx && idx > -1 && members?.splice(idx, 1);
        setActionTaken(null);
        dispatch(turnDrawerOff());
      }
      if (actionTaken === SAVING) {
        const rightToChange = rights?.find(
          (r) => r.organisationId == organisationId
        );
        if (rightToChange) {
          const memb = {
            ...newMemberUser,
            accountUuid: rightToChange.accountUuid,
            userUuid: rightToChange.userUuid,
          } as User;
          dispatch(
            setUserRightsRequest(rightToChange, TablePermission.ALL, memb)
          );
          setActionTaken(null);
          dispatch(turnDrawerOff());
        }
      }
    }
  }, [rights]);

  const onAddUser = () => {
    const rightToChange = rights?.find(
      (r) => r.accountUuid == accountId && r.organisationId == organisationId
    );
    const memberUser = allUsers?.find((m) => m.accountUuid === accountId);
    rightToChange &&
      dispatch(
        setUserRightsRequest(rightToChange, TablePermission.ALL, memberUser)
      );
    dispatch(turnDrawerOff());
  };

  const onMemberChange = (value: any) => {
    dispatch(fetchUserRightsRequest(value));
    setAccountId(value);
  };
  const handleSubmit = (values: FormValues) => {
    const newMember: NewMember = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      isClient,
    };
    const accountType = isClient ? 'CLIENT' : 'STAFF';
    const newMemberUser = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      accountType,
    } as User;
    setActionTaken(SAVING);
    setNewMemberUser(newMemberUser);
    dispatch(addMemberRequest(newMember, true));
    //dispatch(turnDrawerOff());
    //saveNewMember(newMember);
  };

  const memberRemove = (accountId: string) => {
    setActionTaken(DELETING);
    dispatch(fetchUserRightsRequest(accountId));
    setAccountId(accountId);
  };

  const inviteDisabled = () => {
    if (!rights) return true;
    else {
      const idx = rights.findIndex((r) => r.accountUuid === accountId);
      if (idx === -1) return true;
    }
    return false;
  };

  const renderMembersList = () => {
    return (
      <>
        <Typography
          style={{
            fontSize: '18px',
            fontWeight: '400',
            lineHeight: '24px',
            textAlign: 'left',
            color: 'var(--color-text-dark, #042a2a)',
            marginBottom: '15px',
          }}
        >
          {t('users.members')}
        </Typography>
        {members?.map((member) => (
          <RenderUserDetail
            record={member}
            showDelete
            memberRemove={memberRemove}
          />
        ))}
        <ScopeButton //@ts-ignore
          width="100%"
          height="40px"
          type="primary"
          htmlType="submit"
          style={{ marginTop: 40 }}
          onClick={() => setShowInvitationForm(true)}
        >
          {t('users.invite-users')}
        </ScopeButton>
      </>
    );
  };

  const userTypeSelectAfter = (
    <Select
      defaultValue={false}
      onChange={(value) => setIsClient(value)}
      value={isClient}
      style={{ height: '43px' }}
    >
      <Select.Option value={false}>{t('users.user')}</Select.Option>
      <Select.Option value={true} disabled={isClientDisabled}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {t('main.client')}
          {isClientDisabled && <LockOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />}
        </span>
      </Select.Option>
    </Select>
  );

  const renderInviteUserForm = () => {
    return (
      <Form
        name="basic"
        labelCol={{ span: 5 }}
        //wrapperCol={{ span: 14 }}
        //form={form}
        autoComplete="off"
        onFinish={handleSubmit}
      >
        <Row justify={'center'}>
          <Col span={24}>
            <LabelWrapper>
              <span className="label-text">
                {t('users.invite-user-via-email')}
              </span>
            </LabelWrapper>

            <Form.Item
              name="email"
              rules={[
                { required: true, message: t('common.required-field') },
                {
                  type: 'email',
                  message: t('signup.invalid-email'),
                },
              ]}
            >
              <FormInput
                width="100%"
                height={'45px'} //@ts-ignore
                border_color="var(--color-border, #dbeae3)"
                addonAfter={userTypeSelectAfter}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <LabelWrapper>
              <span className="label-text">{t('signup.first-name')}</span>
            </LabelWrapper>
            <Form.Item
              name="firstName"
              rules={[{ required: true, message: t('common.required-field') }]}
            >
              <FormInput
                width="100%" //@ts-ignore
                border_color="var(--color-border, #dbeae3)"
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <LabelWrapper>
              <span className="label-text">{t('signup.last-name')}</span>
            </LabelWrapper>
            <Form.Item
              name="lastName"
              rules={[{ required: true, message: t('common.required-field') }]}
            >
              <FormInput
                width="100%" //@ts-ignore
                border_color="var(--color-border, #dbeae3)"
              />
            </Form.Item>
          </Col>
          <Col span={24}>
          <Form.Item>
            <ScopeButton width="100%" type="primary" htmlType="submit">
              {t('users.send-invitation')}
            </ScopeButton>
          </Form.Item>
          </Col>
        </Row>
      </Form>
    );
  };

  return (
    <>
      {!showInvitationForm && renderMembersList()}

      {showInvitationForm && (
        <>
          {allUsers && allUsers?.length > 0 && (
            <>
              <Form layout="vertical">
                <Row justify={'space-between'}>
                  <Col span={24}>
                    <Form.Item
                      name="staffMember"
                      rules={[
                        {
                          required: true,
                          message: t('common.required-field'),
                        },
                      ]}
                    >
                      <FormSelect //@ts-ignore
                        width="100%"
                        height="45px"
                        bordercolor="var(--color-border, #dbeae3)"
                        placeholder={t('other.select-existing-user')}
                        optionLabelProp="title"
                        onChange={(value: any) => onMemberChange(value)}
                      >
                        {allUsers?.map((member, idx) => (
                          <Select.Option
                            key={idx}
                            title={`${member.firstName} ${member.lastName}`}
                            value={member.accountUuid}
                          >
                            <RenderUserDetail
                              record={member}
                              showOption
                              showRole
                            />
                          </Select.Option>
                        ))}
                      </FormSelect>
                    </Form.Item>
                  </Col>

                  <Col span={24}>
                    <Form.Item>
                      <ScopeButton
                        width="100%"
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        onClick={onAddUser}
                        disabled={inviteDisabled()}
                      >
                        {t('users.add-user')}
                      </ScopeButton>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
              <Divider />
            </>
          )}
          {renderInviteUserForm()}
        </>
      )}
    </>
  );
};
